import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { calculateDateRange } from 'src/utils';

import AdvancedSearchComponent from './components/AdvancedSearch/AdvancedSearchComponent';
import SearchHeaderComponent from './SearchHeaderComponent';

const SearchHeaderContainer = ({
  menuGroups,
  selectedMenuItem,
  onSelectMenuItem,
  searchFormData,
  setSearchFormData,
  showAdvanceSearch,
  setShowAdvanceSearch,
  radioSelected,
  setRadioSelected,
  filterFormData,
  setFilterFormData,
}) => {
  const [dateValidationErrors, setDateValidationErrors] = useState({
    startDate: '',
    endDate: '',
    general: '',
    range: '',
    status: '',
  });
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isSticky, setIsSticky] = useState(false);

  const handleFilterFormEvent = (eventType, payload, errors) => {
    console.log('Event Type:', eventType, 'Payload:', payload);
    switch (eventType) {
      case 'STATUS_SELECT': {
        setDateValidationErrors(() => {
          if (!errors?.status) {
            return {
              ...errors,
            };
          } else {
            return {
              startDate: '',
              endDate: '',
              general: '',
              range: '',
              status: '',
            };
          }
        });
        setFilterFormData((prev) => ({
          ...prev,
          status: payload.value,
        }));
        // Update radio selection based on date changes
        const radioSelected = payload.type;
        setRadioSelected(radioSelected);
        break;
      }
      case 'DATE_RANGE_SELECT': {
        const { startDate, endDate } = calculateDateRange(payload.value);
        setDateValidationErrors({
          startDate: '',
          endDate: '',
          general: '',
          range: '',
          status: '',
        });
        setFilterFormData((prev) => ({
          status: prev.status,
          dateRange: payload.value,
          startRange: startDate,
          endRange: endDate,
        }));
        // Update radio selection based on date changes
        const radioSelected = payload.type;
        setRadioSelected(radioSelected);
        break;
      }
      case 'DATE_CHANGE': {
        // Clear validation errors when dates are changed
        setDateValidationErrors({
          startDate: '',
          endDate: '',
          general: '',
          range: '',
          status: '',
        });
        setFilterFormData((prev) => ({
          status: prev.status,
          startDate: payload.startDate || prev.startDate,
          endDate: payload.endDate || prev.endDate,
        }));
        // Update radio selection based on date changes
        const radioSelected = payload.type;
        setRadioSelected(radioSelected);
        break;
      }
      case 'RADIO_CHANGE': {
        // Clear validation errors when radio selection changes
        setDateValidationErrors({
          startDate: '',
          endDate: '',
          general: '',
          range: '',
          status: '',
        });
        const radioSelected = payload.value;
        setRadioSelected(radioSelected);
        // Reset form data based on radio selection
        const resetData = {
          date: { dateRange: '', status: '' },
          range: { startDate: '', endDate: '', status: '' },
          status: { startDate: '', endDate: '', dateRange: '' },
        };
        setFilterFormData((prev) => ({
          ...prev,
          ...resetData[radioSelected],
        }));
        break;
      }
      default:
        console.warn(`Unhandled event type: ${eventType}`);
    }
  };

  const handleOnChange = (value) => {
    if (typeof value === 'string') {
      setSearchFormData((prev) => ({ ...prev, searchText: value }));
    } else {
      setSearchFormData((prev) => ({ ...prev, ...value }));
      // setSelectedOption(value);
    }
  };

  // Add keyboard event handler
  const handleKeyPress = (event) => {
    // Check if Enter key is pressed and searchText has content
    if (event.key === 'Enter' && searchFormData.searchText?.trim()) {
      event.preventDefault(); // Prevent form submission if inside a form
      handleSearchButtonClick(); // Trigger search
    }
  };

  const handleAdvancedSearch = () => {
    setShowAdvanceSearch((prev) => {
      // if (prev === true) {
      //   if (
      //     selectedMenuItem?.filter?.status ||
      //     selectedMenuItem?.filter?.dateRange ||
      //     selectedMenuItem?.filter?.startDate ||
      //     selectedMenuItem?.filter?.endDate
      //   ) {
      //     handleSearchButtonClick('clearAll');
      //   }
      //   // Reset advanced search state when it's hidden
      //   setRadioSelected('');
      //   setFilterFormData({
      //     dateRange: '',
      //     status: '',
      //     startDate: '',
      //     endDate: '',
      //   });
      // }
      return !prev;
    });
  };

  const validateSearchForm = () => {
    const errors = {
      startDate: '',
      endDate: '',
      general: '',
      range: '',
      status: '',
    };

    // Only validate if advanced search is shown and date mode is selected
    if (!showAdvanceSearch) {
      setDateValidationErrors(errors);
      return true;
    }

    switch (radioSelected) {
      case 'date': {
        const { startDate, endDate } = filterFormData;

        // Check if both dates are provided
        if (!startDate && !endDate) {
          errors.general =
            'Both start and end dates are required for date search.';
          setDateValidationErrors(errors);
          return false;
        }

        if (!startDate) {
          errors.startDate = 'Start date is required.';
          setDateValidationErrors(errors);
          return false;
        }

        if (!endDate) {
          errors.endDate = 'End date is required.';
          setDateValidationErrors(errors);
          return false;
        }

        // Parse dates for comparison
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Check for invalid dates
        if (isNaN(start.getTime())) {
          errors.startDate = 'Please enter a valid start date.';
          setDateValidationErrors(errors);
          return false;
        }

        if (isNaN(end.getTime())) {
          errors.endDate = 'Please enter a valid end date.';
          setDateValidationErrors(errors);
          return false;
        }

        // Check if start date is before end date
        if (start > end) {
          errors.endDate = 'End date cannot be earlier than start date.';
          setDateValidationErrors(errors);
          return false;
        }

        // Check if date range exceeds 1 year
        const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
        if (end.getTime() - start.getTime() > oneYearInMs) {
          errors.general = 'Date range cannot exceed 1 year.';
          setDateValidationErrors(errors);
          return false;
        }
        break;
      }
      case 'range': {
        const { dateRange } = filterFormData;

        if (!dateRange || dateRange.trim() === '') {
          errors.range = 'Date range selection is required.';
          setDateValidationErrors(errors);
          return false;
        }
        break;
      }
      case 'status': {
        const { status } = filterFormData;
        if (
          dateValidationErrors.general ||
          dateValidationErrors.startDate ||
          dateValidationErrors.endDate
        ) {
          return false;
        }
        if (!status || status.trim() === '') {
          errors.status = 'Status selection is required.';
          setDateValidationErrors(errors);
          return false;
        }
        break;
      }
      default: {
        errors.general = 'Please select appropriate search filter type.';
        errors.range = 'Please select appropriate search filter type.';
        errors.status = 'Please select appropriate search filter type.';
        setDateValidationErrors(errors);
        return false;
      }
    }

    setDateValidationErrors(errors);
    return true;
  };

  const handleSearchButtonClick = (actionType) => {
    if (actionType === 'clearAll') {
      onSelectMenuItem((prev) => ({
        ...prev,
        searchText: '',
        filter: {},
      }));
      // Reset advanced search state when it's hidden
      setRadioSelected('');
      setFilterFormData({
        dateRange: '',
        status: '',
        startDate: '',
        endDate: '',
      });
      setShowAdvanceSearch(false);
      return;
    }
    // Validate form before executing search
    if (!validateSearchForm()) {
      console.log('Search validation failed:', dateValidationErrors);
      return;
    }

    onSelectMenuItem({
      searchText: searchFormData.searchText,
      category: {
        name: searchFormData.category,
        categoryNr: searchFormData.categoryNr,
      },
      subcategory: searchFormData.subcategoryNr
        ? {
            name: searchFormData.label,
            subcategoryNr: searchFormData.subcategoryNr,
          }
        : null,
      // Include filter data when advanced search is active
      ...(showAdvanceSearch && {
        filter: {
          radioSelected,
          ...filterFormData,
        },
      }),
    });
    setShowAdvanceSearch(false);
    console.log('Search Payload', searchFormData, filterFormData);
  };

  useEffect(() => {
    setSearchFormData((prev) => ({
      ...prev,
      searchText: selectedMenuItem?.searchText,
      category: selectedMenuItem?.category?.name,
      categoryNr: selectedMenuItem?.category?.categoryNr,
      label: selectedMenuItem?.subcategory?.name,
      subcategoryNr: selectedMenuItem?.subcategory?.subcategoryNr,
    }));
    if (selectedMenuItem?.resetFilter) {
      // Reset advanced search state when it's hidden
      setShowAdvanceSearch(false);
      setRadioSelected('');
      setFilterFormData({
        dateRange: '',
        status: '',
        startDate: '',
        endDate: '',
      });
    }
  }, [selectedMenuItem]);

  // Calculate header height
  useEffect(() => {
    const headerEle = document.querySelector('header.header');
    const messagingBar = document.querySelector('body.has-messaging-bar');
    let height = 0;
    if (headerEle) {
      height = headerEle.clientHeight;
      if (messagingBar) {
        height += 35;
      }
      setHeaderHeight(height);
    }

    const handleScroll = () => {
      if (window?.scrollY > height + 24) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      className={`search-container ${isSticky ? 'search-header-sticky' : ''}`}
      style={{ top: `${headerHeight}px` }}
    >
      <SearchHeaderComponent
        searchFormData={searchFormData}
        menuGroups={menuGroups}
        selectedMenuItem={selectedMenuItem}
        onSelectMenuItem={onSelectMenuItem}
        handleOnChange={handleOnChange}
        handleSearchButtonClick={handleSearchButtonClick}
        handleAdvancedSearch={handleAdvancedSearch}
        handleKeyPress={handleKeyPress}
      />
      {showAdvanceSearch && (
        <>
          {/* {dateValidationErrors.general && !radioSelected && (
            <div className="alert alert-danger mt-2" role="alert">
              <strong>Search Error:</strong> {dateValidationErrors.general}
            </div>
          )} */}
          <AdvancedSearchComponent
            radioSelected={radioSelected}
            filterFormData={filterFormData}
            handleFilterFormEvent={handleFilterFormEvent}
            validationErrors={dateValidationErrors}
            handleAdvancedSearch={handleAdvancedSearch}
          />
        </>
      )}
    </div>
  );
};

SearchHeaderContainer.propTypes = {
  selectedMenuItem: PropTypes.object,
  onSelectMenuItem: PropTypes.func,
  menuGroups: PropTypes.array.isRequired,
  searchFormData: PropTypes.object,
  setSearchFormData: PropTypes.func,
  showAdvanceSearch: PropTypes.bool,
  setShowAdvanceSearch: PropTypes.func,
  radioSelected: PropTypes.string,
  setRadioSelected: PropTypes.func,
  filterFormData: PropTypes.object,
  setFilterFormData: PropTypes.func,
};

export default SearchHeaderContainer;
